import React from 'react';
import { motion } from 'motion/react';
import { X, Check, Loader2 } from 'lucide-react';
import { FairnessFinding, FAIRNESS_VISIBLE_CAP } from '../../lib/fairness-check';

type InningFlowStep = 'log_pitches' | 'select_pitcher' | 'next_pitcher' | 'fairness_review' | 'set_defense' | null;

interface InningFlowModalProps {
  inningFlowStep: InningFlowStep;
  setInningFlowStep: (step: InningFlowStep) => void;
  currentInning: number;
  currentLoggingPitcherId: string | null;
  setCurrentLoggingPitcherId: (id: string | null) => void;
  inningPitches: string;
  setInningPitches: (pitches: string) => void;
  handleSavePitches: () => void;
  handleAddAnotherPitcher: () => void;
  lineup: any[];
  currentPitcher: any;
  handlePitcherContinue: (willContinue: boolean) => void;
  /** The pitcher the coach's plan has on the mound next inning, when that is a change. */
  plannedNextPitcher: any | null;
  onBringInPlannedPitcher: (playerId: string) => void;
  fairnessFindings: FairnessFinding[];
  onAutoBalance: () => void;
  isAutoBalancing: boolean;
}

export const InningFlowModal = ({
  inningFlowStep,
  setInningFlowStep,
  currentInning,
  currentLoggingPitcherId,
  setCurrentLoggingPitcherId,
  inningPitches,
  setInningPitches,
  handleSavePitches,
  handleAddAnotherPitcher,
  lineup,
  currentPitcher,
  handlePitcherContinue,
  plannedNextPitcher,
  onBringInPlannedPitcher,
  fairnessFindings,
  onAutoBalance,
  isAutoBalancing
}: InningFlowModalProps) => {
  if (!inningFlowStep || inningFlowStep === 'set_defense') return null;

  return (
    <>
      {inningFlowStep === 'log_pitches' && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div 
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-surface w-full max-w-sm rounded-3xl p-6 border border-hairline"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-black italic uppercase">End Inning {currentInning}</h3>
              <button onClick={() => setInningFlowStep(null)} className="text-ink-2 hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-[10px] font-bold text-ink-2 uppercase tracking-widest mb-2">
                How many pitches did {lineup.find(p => p.id === currentLoggingPitcherId)?.name} throw?
              </label>
              <input
                type="number"
                value={inningPitches}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  if (!val) { setInningPitches(''); return; }
                  const num = parseInt(val, 10);
                  setInningPitches(String(Math.min(Math.max(num, 0), 200)));
                }}
                placeholder="e.g. 15"
                className="w-full bg-raised border border-hairline rounded-xl px-4 py-3 text-ink font-black text-xl outline-none focus:border-emerald-500 transition-colors"
                autoFocus
              />
              {!inningPitches && (
                <p className="text-[9px] text-ink-2 mt-1">Enter pitches thrown this inning (0–200)</p>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={handleSavePitches}
                disabled={!inningPitches}
                className="w-full py-3 bg-emerald-500 text-ink font-black rounded-xl uppercase italic hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save & Continue
              </button>
              <button 
                onClick={handleAddAnotherPitcher}
                className="w-full py-3 bg-hairline text-ink font-black rounded-xl uppercase italic hover:bg-track transition-colors text-sm"
              >
                Add Another Pitcher
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {inningFlowStep === 'select_pitcher' && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-canvas flex flex-col"
        >
          <header className="p-6 border-b border-hairline flex items-center justify-between bg-raised sticky top-0 z-20">
            <div>
              <h2 className="text-xl font-black italic uppercase">Select Pitcher</h2>
              <p className="text-[10px] font-bold text-ink-2 uppercase tracking-widest">Who else pitched?</p>
            </div>
            <button onClick={() => setInningFlowStep('next_pitcher')} className="p-2 bg-surface rounded-lg text-ink-2 hover:text-ink">
              <X className="w-5 h-5" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-6 space-y-2">
            {lineup.map((player, index) => (
              <button
                key={`${player.id}-${index}`}
                onClick={() => {
                  setCurrentLoggingPitcherId(player.id);
                  setInningFlowStep('log_pitches');
                }}
                className="w-full bg-surface p-4 rounded-2xl border border-hairline flex items-center space-x-4 hover:border-emerald-500/50 transition-colors text-left"
              >
                <div className="w-10 h-10 bg-hairline rounded-xl flex items-center justify-center font-black text-ink-2">
                  #{player.number}
                </div>
                <div className="font-black uppercase text-sm">{player.name}</div>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {inningFlowStep === 'next_pitcher' && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div 
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-surface w-full max-w-sm rounded-3xl p-6 border border-hairline text-center"
          >
            <h3 className="text-xl font-black italic uppercase mb-2">Next Inning</h3>
            {plannedNextPitcher ? (
              // The coach's own bullpen plan calls for a change here. Following it
              // is one tap; keeping the current pitcher is the other. Nothing is
              // swapped until he chooses.
              <>
                <p className="text-ink-2 text-sm mb-8">
                  Your plan has {plannedNextPitcher.name.split(' ')[0]} pitching inning {currentInning + 1}.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => onBringInPlannedPitcher(plannedNextPitcher.id)}
                    className="py-3 bg-emerald-500 text-ink font-black rounded-xl uppercase italic hover:bg-emerald-400 transition-colors"
                  >
                    Bring in {plannedNextPitcher.name.split(' ')[0]}
                  </button>
                  <button
                    onClick={() => handlePitcherContinue(true)}
                    className="py-3 bg-hairline text-ink font-black rounded-xl uppercase italic hover:bg-track transition-colors"
                  >
                    Keep {currentPitcher?.name?.split(' ')[0] || 'pitcher'}
                  </button>
                </div>
                <button
                  onClick={() => handlePitcherContinue(false)}
                  className="w-full mt-4 text-sm font-semibold text-ink-2"
                >
                  Pick someone else
                </button>
              </>
            ) : (
            <>
            <p className="text-ink-2 text-sm mb-8">
              Will {currentPitcher?.name || 'the current pitcher'} start Inning {currentInning + 1} on the mound?
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => handlePitcherContinue(true)}
                className="py-3 bg-emerald-500 text-ink font-black rounded-xl uppercase italic hover:bg-emerald-400 transition-colors"
              >
                Yes
              </button>
              <button 
                onClick={() => handlePitcherContinue(false)}
                className="py-3 bg-hairline text-ink font-black rounded-xl uppercase italic hover:bg-track transition-colors"
              >
                No
              </button>
            </div>
            </>
            )}
          </motion.div>
        </motion.div>
      )}

      {inningFlowStep === 'fairness_review' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-surface w-full max-w-sm rounded-3xl p-6 border border-hairline"
          >
            <div className="mb-5">
              <p className="text-[10px] font-black text-gold-ink uppercase tracking-widest mb-1">
                Fairness Check
              </p>
              <h3 className="text-xl font-black italic uppercase leading-tight">
                Through Inning {currentInning}
              </h3>
            </div>

            {fairnessFindings.length === 0 ? (
              // Empty state — rules are on but nothing's off. Reassure the coach
              // the check ran and the bench/field are balanced.
              <div className="flex items-center space-x-3 mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-sm font-bold text-emerald-300">All balanced. Playing time looks even.</p>
              </div>
            ) : (
              <div className="space-y-3 mb-6">
                {fairnessFindings.slice(0, FAIRNESS_VISIBLE_CAP).map((f, i) => (
                  <div key={`${f.rule}-${i}`} className="flex items-start space-x-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 flex-shrink-0" />
                    <p className="text-sm text-ink-2 leading-relaxed">{f.message}</p>
                  </div>
                ))}
                {fairnessFindings.length > FAIRNESS_VISIBLE_CAP && (
                  <p className="text-xs text-ink-2 pl-5">
                    +{fairnessFindings.length - FAIRNESS_VISIBLE_CAP} more to keep an eye on
                  </p>
                )}
              </div>
            )}

            {fairnessFindings.length === 0 ? (
              <button
                onClick={() => setInningFlowStep('set_defense')}
                className="w-full py-3 bg-emerald-500 text-ink font-black rounded-xl uppercase italic hover:bg-emerald-400 transition-colors"
              >
                Got it
              </button>
            ) : (
              // Two compact side-by-side actions. "Auto Change" runs the same
              // OVR-aware, fairness-respecting engine the Auto modes use, then
              // lands the coach on the diamond — still editable. "Ignore for Now"
              // proceeds to set defense by hand.
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setInningFlowStep('set_defense')}
                  className="py-3 bg-hairline text-ink font-black rounded-xl uppercase italic text-sm hover:bg-track transition-colors"
                >
                  Ignore for Now
                </button>
                <button
                  onClick={onAutoBalance}
                  disabled={isAutoBalancing}
                  className="py-3 bg-emerald-500 text-ink font-black rounded-xl uppercase italic text-sm hover:bg-emerald-400 transition-colors disabled:opacity-60 flex items-center justify-center space-x-2"
                >
                  {isAutoBalancing && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isAutoBalancing ? '…' : 'Auto Change'}</span>
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </>
  );
};
